import React, { useState, useEffect } from 'react';
import { Link } from 'react-router';
import {
  HomeOutlined,
  TagOutlined,
  ContactsOutlined,
  CreditCardOutlined,
  FundProjectionScreenOutlined,
  ThunderboltOutlined,
  BranchesOutlined,
  SettingOutlined,
  CalendarOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { Layout, Menu, Tooltip } from 'antd';
import { FormattedMessage } from 'react-intl';
import config from 'config';
import styled from 'styled-components';

import Logo from 'Common/Components/Logo';
import { useCalendarRedirect } from 'Home/hooks/calendarRedirect';

const { IN_PRODUCTION } = config;
const { Sider } = Layout;
const MenuItem = Menu.Item;

const AsideMenu = styled(Sider)`
  left: 0;
  height: 100vh;
  position: fixed;
  background-color: #fff;
  .ant-menu-item-disabled {
    pointer-events: none;
  }
  .ant-menu-submenu-disabled {
    opacity: 0.4;
    pointer-events: none;
  }
  .ant-menu-item-only-child {
    padding-left: 48px !important;
    margin-top: 10px !important;
  }

  .ant-menu-item {
    line-height: 48px !important;
    height: 48px !important;
  }

  .ant-menu-item-selected {
    background-color: #5556ff1a !important;
    border-radius: 8px !important;
  }
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  & ::-webkit-scrollbar {
    display: none;
  }
  .ant-layout-sider-children {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  }
  .ant-menu {
    flex: 1;
    overflow-y: auto;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
  .ant-menu-submenu {
    line-height: 48px !important;
    margin: 20px 0px !important;
  }
  .ant-menu-item-disabled {
    opacity: 0.4 !important;
  }
  @media (max-width: 768px) {
    height: 100%;
  }
`;

const SubMenu = Menu.SubMenu;

interface SideMenuProps {
  collapsed: boolean;
  screenName: string;
  isAccountActive: boolean;
  accountSource: string | null;
  isShorePayUser: boolean;
  isCustomerDisplayUser: boolean;
  isBetaUser: boolean;
  isPreAccountingUser: boolean;
  isGiftCardUser: boolean;
  isShopUser: boolean;
  isAPIKeysUser: boolean;
  isDatevUser: boolean;
  country: string;
}

const calendarFallbackUrl = IN_PRODUCTION
  ? 'https://my.shore.com/calendar/week'
  : 'https://app-shell-staging.shore.com/calendar/week';

const menuKeys = [
  'start-page',
  'inventory',
  'contacts',
  'reports',
  'shorepay',
  'api-apps',
  'integrations',
  'settings',
  'calendar-bookings',
];

const SideMenu: React.FC<SideMenuProps> = ({
  screenName = '',
  collapsed,
  isAccountActive,
  accountSource,
  isShorePayUser,
  isBetaUser,
  isCustomerDisplayUser,
  isPreAccountingUser,
  isGiftCardUser,
  isShopUser,
  isAPIKeysUser,
  isDatevUser,
  country,
}) => {
  const currentActive = screenName.toLowerCase();
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [selectedKey, setSelectedKey] = useState('');
  const calendarRedirectMutation = useCalendarRedirect();

  const handleCalendarBookingsClick = () => {
    if (calendarRedirectMutation.isPending) return;
    const newWindow = window.open('', '_blank');
    if (newWindow) newWindow.opener = null;

    const goTo = (url: string) => {
      if (newWindow) {
        newWindow.location.href = url;
      } else {
        window.location.href = url;
      }
    };

    calendarRedirectMutation.mutate(undefined, {
      onSuccess: (data) => goTo(data.redirect_url || calendarFallbackUrl),
      onError: () => goTo(calendarFallbackUrl),
    });
  };
  const allowCustomerDisplay = isCustomerDisplayUser || isBetaUser;
  const isAccFromDe = country === 'DE';
  const isAccFromAt = country === 'AT';
  const isAccFromSwiss = country === 'CH';

  useEffect(() => {
    setSelectedKey(screenName.toLowerCase());
  }, [screenName]);

  useEffect(() => {
    if (screenName === 'start-page' || screenName === 'api-apps') {
      setOpenKeys([screenName]);
    }
  }, [screenName]);

  useEffect(() => {
    if (collapsed) {
      setOpenKeys([]);
    } else {
      setOpenKeys([currentActive.split('/')[0]]);
    }
  }, [collapsed, currentActive]);

  const onOpenChange = (keys: any[]) => {
    const latestOpenKey = keys.find((key) => openKeys.indexOf(key) === -1);
    if (menuKeys.indexOf(latestOpenKey) === -1) {
      setOpenKeys(keys);
    } else {
      setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
    }
  };

  return (
    <AsideMenu
      width={256}
      trigger={null}
      collapsible
      collapsed={window.innerWidth <= 768 ? true : collapsed}
    >
      <Logo collapsed={window.innerWidth <= 768 ? true : collapsed} />
      <Menu
        theme="light"
        mode="inline"
        selectedKeys={[selectedKey]}
        openKeys={openKeys}
        onOpenChange={onOpenChange}
      >
        <MenuItem key="start-page" icon={<HomeOutlined />}>
          <Link to="/">
            <span>
              <FormattedMessage
                defaultMessage="Home"
                description="side menu link"
              />
            </span>
          </Link>
        </MenuItem>
        <SubMenu
          key="inventory"
          icon={<TagOutlined />}
          title={<FormattedMessage defaultMessage="Inventory" />}
          disabled={!isAccountActive}
        >
          <MenuItem key="inventory/products">
            <Link to="/inventory/products">
              <FormattedMessage defaultMessage="Products" />
            </Link>
          </MenuItem>
          <MenuItem key="inventory/services">
            <Link to="/inventory/services">
              <FormattedMessage defaultMessage="Services" />
            </Link>
          </MenuItem>
          <MenuItem key="inventory/giftcards" disabled={!isGiftCardUser}>
            <Link to="/inventory/giftcards">
              <FormattedMessage defaultMessage="Giftcards" />
            </Link>
          </MenuItem>
          <MenuItem key="inventory/categories">
            <Link to="/inventory/categories">
              <FormattedMessage defaultMessage="Categories" />
            </Link>
          </MenuItem>
          <MenuItem key="inventory/stocktaking">
            <Link to="/inventory/stocktaking">
              <FormattedMessage defaultMessage="Stocktaking" />
            </Link>
          </MenuItem>
        </SubMenu>
        <SubMenu
          key="contacts"
          icon={<ContactsOutlined />}
          title={<FormattedMessage defaultMessage="Contacts" />}
          disabled={!isAccountActive}
        >
          <MenuItem key="contacts/customers">
            <Link to="/contacts/customers">
              <FormattedMessage defaultMessage="Customers" />
            </Link>
          </MenuItem>
          <MenuItem key="contacts/users">
            <Link to="/contacts/users">
              <FormattedMessage defaultMessage="Users" />
            </Link>
          </MenuItem>
        </SubMenu>
        <SubMenu
          key="reports"
          icon={<FundProjectionScreenOutlined />}
          title={<FormattedMessage defaultMessage="Reports" />}
        >
          <MenuItem key="reports/dashboard">
            <Link to="/reports/dashboard">
              <FormattedMessage defaultMessage="Dashboard" />
            </Link>
          </MenuItem>
          <MenuItem key="reports/live">
            <Link to="/reports/live">
              <FormattedMessage defaultMessage="Live Reports" />
            </Link>
          </MenuItem>
          {isAccFromDe && (
            <MenuItem key="reports/cashbook">
              <Link to="/reports/cashbook">
                <FormattedMessage defaultMessage="Cashbook" />
              </Link>
            </MenuItem>
          )}
          <MenuItem key="reports/downloads">
            <Link to="/reports/downloads">
              <FormattedMessage defaultMessage="Downloads" />
            </Link>
          </MenuItem>
        </SubMenu>
        {(isAccFromDe || isAccFromAt || isAccFromSwiss) && (
          <SubMenu
            key="shorepay"
            icon={<CreditCardOutlined />}
            title={<FormattedMessage defaultMessage="Shore Pay" />}
          >
            <MenuItem key="shorepay/account">
              <Link to="/shorepay/account">
                <FormattedMessage defaultMessage="My Account" />
              </Link>
            </MenuItem>
            {!IN_PRODUCTION && (
              <MenuItem key="shorepay/readers">
                <Link to="/shorepay/readers">
                  <FormattedMessage defaultMessage="Readers" />
                </Link>
              </MenuItem>
            )}
            <MenuItem key="shorepay/reports">
              <Link to="/shorepay/reports">
                <FormattedMessage defaultMessage="Reports" />
              </Link>
            </MenuItem>
            {isShorePayUser && (
              <MenuItem key="shorepay/settings">
                <Link to="/shorepay/settings">
                  <FormattedMessage defaultMessage="Settings" />
                </Link>
              </MenuItem>
            )}
          </SubMenu>
        )}
        <MenuItem
          key="api-apps"
          icon={<ThunderboltOutlined />}
          title={<FormattedMessage defaultMessage="API & Apps" />}
          disabled={!isAccountActive || !isAPIKeysUser}
        >
          <Link to="/api-apps">
            <FormattedMessage defaultMessage="API & Apps" />
          </Link>
        </MenuItem>
        {(isAccFromDe || isAccFromAt) && (
          <SubMenu
            key="integrations"
            icon={<BranchesOutlined />}
            title={<FormattedMessage defaultMessage="Integrations" />}
          >
            {isAccFromDe && isDatevUser && (
              <MenuItem
                key="integrations/datev/home"
                title={<FormattedMessage defaultMessage="Datev Kassenarchiv" />}
              >
                <Link to="/integrations/datev/home">
                  <FormattedMessage defaultMessage="Datev Kassenarchiv" />
                </Link>
              </MenuItem>
            )}
            {isAccFromAt && (
              <MenuItem key="integrations/rksv">
                <Link to="/integrations/rksv">
                  <FormattedMessage defaultMessage="RKSV" />
                </Link>
              </MenuItem>
            )}

            {isAccFromDe && (
              <MenuItem key="integrations/kassensichv">
                <Link to="/integrations/kassensichv">
                  <FormattedMessage defaultMessage="KassenSichV" />
                </Link>
              </MenuItem>
            )}
          </SubMenu>
        )}

        <SubMenu
          key="settings"
          icon={<SettingOutlined />}
          title={<FormattedMessage defaultMessage="Settings" />}
        >
          <MenuItem key="settings/general">
            <Link to="/settings/general">
              <FormattedMessage defaultMessage="General" />
            </Link>
          </MenuItem>
          <MenuItem key="settings/store">
            <Link to="/settings/store">
              <FormattedMessage defaultMessage="Store" />
            </Link>
          </MenuItem>

          {isAccFromDe &&
            (!isPreAccountingUser ? (
              <Tooltip
                title={
                  <FormattedMessage defaultMessage="Available with Plus and Premium" />
                }
              >
                <div style={{ width: '100%' }}>
                  <MenuItem key="settings/accounting" disabled>
                    <Link to="/settings/accounting">
                      <FormattedMessage defaultMessage="Accounting" />
                    </Link>
                  </MenuItem>
                </div>
              </Tooltip>
            ) : (
              <MenuItem key="settings/accounting">
                <Link to="/settings/accounting">
                  <FormattedMessage defaultMessage="Accounting" />
                </Link>
              </MenuItem>
            ))}
          <MenuItem key="settings/e-commerce" disabled={!isShopUser}>
            <Link to="/settings/e-commerce">
              <FormattedMessage defaultMessage="E-Commerce" />
            </Link>
          </MenuItem>
          {!allowCustomerDisplay ? (
            <Tooltip
              title={
                !allowCustomerDisplay && (
                  <FormattedMessage defaultMessage="Available with Plus and above" />
                )
              }
            >
              <div style={{ width: '100%' }}>
                <MenuItem
                  key="settings/customer-display"
                  disabled={!allowCustomerDisplay}
                >
                  <Link to="/settings/customer-display">
                    <FormattedMessage defaultMessage="Customer Display" />
                  </Link>
                </MenuItem>
              </div>
            </Tooltip>
          ) : (
            <MenuItem
              key="settings/customer-display"
              disabled={!allowCustomerDisplay}
            >
              <Link to="/settings/customer-display">
                <FormattedMessage defaultMessage="Customer Display" />
              </Link>
            </MenuItem>
          )}
        </SubMenu>
        {isAccountActive && accountSource && accountSource === 'shore' && (
          <MenuItem
            key="calendar-bookings"
            icon={
              calendarRedirectMutation.isPending ? (
                <LoadingOutlined />
              ) : (
                <CalendarOutlined />
              )
            }
            title={<FormattedMessage defaultMessage="Calendar & Bookings" />}
            disabled={calendarRedirectMutation.isPending}
            onClick={handleCalendarBookingsClick}
          >
            <FormattedMessage defaultMessage="Calendar & Bookings" />
          </MenuItem>
        )}
      </Menu>
    </AsideMenu>
  );
};

export default SideMenu;
